import { type Color } from '../math/Color';

/** The primitives debug draw can emit. Deliberately three, because every overlay so far is these. */
export const DebugShapeKind = {
  /** A straight segment between two world points. */
  Line: 'line',
  /** A rectangle placed by its center, optionally rotated. */
  Rect: 'rect',
  /** A circle placed by its center. */
  Circle: 'circle',
} as const;

/** One of the {@link DebugShapeKind} values. */
export type DebugShapeKind = (typeof DebugShapeKind)[keyof typeof DebugShapeKind];

/**
 * What every debug shape carries.
 *
 * The color is the caller's instance rather than a copy, which keeps a frame of debug draw free of
 * allocations. It is read while the frame's shape list is, so a color handed to debug draw must not be
 * mutated until the next frame begins.
 */
interface DebugShapeBase {
  /** Which shape this is; narrow the union on it. */
  readonly kind: DebugShapeKind;
  /** Stroke color, in world-independent RGBA. */
  readonly color: Color;
}

/** A straight segment between two world points. */
export interface DebugLine extends DebugShapeBase {
  readonly kind: typeof DebugShapeKind.Line;
  /** World X the segment starts at. */
  readonly startX: number;
  /** World Y the segment starts at. */
  readonly startY: number;
  /** World X the segment ends at. */
  readonly endX: number;
  /** World Y the segment ends at. */
  readonly endY: number;
}

/** A rectangle placed by its center, in world units. */
export interface DebugRect extends DebugShapeBase {
  readonly kind: typeof DebugShapeKind.Rect;
  /** World X of the center. */
  readonly centerX: number;
  /** World Y of the center. */
  readonly centerY: number;
  /** Extent along the rectangle's own X axis. */
  readonly width: number;
  /** Extent along the rectangle's own Y axis. */
  readonly height: number;
  /** Rotation about the center in radians; zero leaves the rectangle axis aligned. */
  readonly rotation: number;
}

/** A circle placed by its center, in world units. */
export interface DebugCircle extends DebugShapeBase {
  readonly kind: typeof DebugShapeKind.Circle;
  /** World X of the center. */
  readonly centerX: number;
  /** World Y of the center. */
  readonly centerY: number;
  /** Radius in world units. */
  readonly radius: number;
}

/** Anything debug draw can put in a frame's shape list. */
export type DebugShape = DebugLine | DebugRect | DebugCircle;
